"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon, PencilIcon, Trash2Icon, BanIcon } from "lucide-react";

import type { FacturationExclusionRule } from "@/prisma/generated/client";
import {
  invalidateFacturationExclusionRulesQuery,
  useFacturationExclusionRulesQuery,
} from "@/lib/queries";
import { deleteFacturationExclusionRule } from "@/app/lib/actions/facturation-exclusion-rules";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import FacturationExclusionRuleForm from "./facturation-exclusion-rule-form";

function formatDate(value: Date | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("fr-FR");
}

function AdministrationFacturationExclusionRules() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useFacturationExclusionRulesQuery({
    sort: { effectiveStartDate: "desc" },
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] =
    useState<FacturationExclusionRule | null>(null);
  const [deletingRule, setDeletingRule] =
    useState<FacturationExclusionRule | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const refresh = () => invalidateFacturationExclusionRulesQuery(queryClient);

  const openCreate = () => {
    setEditingRule(null);
    setFormOpen(true);
  };

  const openEdit = (rule: FacturationExclusionRule) => {
    setEditingRule(rule);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRule) return;
    setDeleteLoading(true);
    await deleteFacturationExclusionRule({ id: deletingRule.id });
    setDeleteLoading(false);
    setDeletingRule(null);
    refresh();
  };

  const rules = data?.facturationExclusionRules ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BanIcon className="text-primary size-5" />
                Exclusions facturation
                {!isLoading ? (
                  <span className="text-muted-foreground text-sm font-normal">
                    ({total} entrées)
                  </span>
                ) : null}
              </CardTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                Paramétrage des exclusions FDM / VN par marque, nature de vente
                et période d&apos;application.
              </p>
            </div>
            <Button size="sm" onClick={openCreate}>
              <PlusIcon className="mr-1.5 size-4" />
              Nouvelle règle
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isError ? (
            <p className="px-6 py-4 text-sm text-red-600">
              Erreur lors du chargement des exclusions de facturation.
            </p>
          ) : null}

          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              Chargement...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">M. num</TableHead>
                  <TableHead className="w-20">M. alpha</TableHead>
                  <TableHead>Libellé marque</TableHead>
                  <TableHead className="w-20">N. vente</TableHead>
                  <TableHead className="w-20">Code</TableHead>
                  <TableHead className="w-36">Début</TableHead>
                  <TableHead className="w-36">Fin</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-mono">
                      {rule.brandNumericCode}
                    </TableCell>
                    <TableCell className="font-mono">
                      {rule.brandAlphabeticCode}
                    </TableCell>
                    <TableCell>{rule.brandAlphabeticLabel}</TableCell>
                    <TableCell className="font-mono">
                      {rule.saleNature}
                    </TableCell>
                    <TableCell className="font-mono">
                      {rule.billingCode}
                    </TableCell>
                    <TableCell>{formatDate(rule.effectiveStartDate)}</TableCell>
                    <TableCell>{formatDate(rule.effectiveEndDate)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary size-7"
                          onClick={() => openEdit(rule)}
                        >
                          <PencilIcon className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-7"
                          onClick={() => setDeletingRule(rule)}
                        >
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-muted-foreground py-6 text-center"
                    >
                      Aucune règle active.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}

          {!isLoading && rules.length > 0 ? (
            <div className="text-muted-foreground border-t px-6 py-3 text-xs">
              {total} règles actives · Versionnement actif
            </div>
          ) : null}
        </CardContent>
      </Card>

      <FacturationExclusionRuleForm
        key={editingRule?.id ?? "new"}
        rule={editingRule}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={refresh}
      />

      <AlertDialog
        open={!!deletingRule}
        onOpenChange={(isOpen) => !isOpen && setDeletingRule(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la règle ?</AlertDialogTitle>
            <AlertDialogDescription>
              La règle sera désactivée (soft delete) et historisée via le
              versionnement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { AdministrationFacturationExclusionRules };
